<template>
  <template v-if="$route.name === 'teacher-lessons.index'">
    <div class="container-fluid px-0" v-can="'teacher_lessons.display'">
      <TeacherLessonFilters @filter="handleFilter" />

      <div class="col-12 col-xl-12">
        <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
          <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
            <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
              <h6 class="text-white text-capitalize ps-3 pe-3" style="font-size: 1rem">
                {{ $t('teacherLessons.title') }}
              </h6>
            </div>
          </div>

          <div class="card-body px-0 pb-0 pb-2 pe-xl-3">
            <LoadingSpinner v-if="isLoading && lessons.length === 0" :message="$t('common.loading')" class="py-5" />

            <template v-else>
              <!-- Desktop table -->
              <div class="table-responsive p-1 p-xl-4 d-none d-xl-block">
                <table class="table align-items-center mb-0">
                  <thead>
                    <tr>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('teacherLessons.teacher') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('teacherLessons.classGroupSubject') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('teacherLessons.date') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('teacherLessons.price') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('teacherLessons.status') }}</th>
                      <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">{{ $t('common.actions') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-if="!isLoading && lessons.length === 0">
                      <td colspan="6" class="text-center py-4 text-muted">
                        <i class="fas fa-chalkboard fa-2x mb-2"></i>
                        <p>{{ $t('teacherLessons.noRecords') }}</p>
                      </td>
                    </tr>
                    <tr v-for="lesson in lessons" :key="lesson.id">
                      <td><span class="text-sm">{{ lesson.teacher?.name || '—' }}</span></td>
                      <td>
                        <span class="text-xs">
                          {{ lesson.class?.name || '—' }} / {{ lesson.group?.name || '—' }} / {{ lesson.subject?.name || '—' }}
                        </span>
                      </td>
                      <td><span class="text-sm">{{ lesson.lesson_date }}</span></td>
                      <td class="text-center"><span class="text-sm">{{ lesson.lesson_price }} {{ $t('common.currency') }}</span></td>
                      <td class="text-center">
                        <select class="form-select form-select-sm" v-model="lesson.status" @change="updateStatus(lesson)">
                          <option value="attended">{{ $t('teacherLessons.attended') }}</option>
                          <option value="absent">{{ $t('teacherLessons.absent') }}</option>
                          <option value="cancelled">{{ $t('teacherLessons.cancelled') }}</option>
                        </select>
                      </td>
                      <td class="text-center pe-4">
                        <button class="btn btn-link text-danger p-1 mx-1" @click="removeLesson(lesson)" v-can="'teacher_lessons.delete'">
                          <i class="fas fa-trash fa-sm"></i>
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Mobile cards -->
              <div class="d-block d-xl-none px-2 pt-1">
                <div v-if="!isLoading && lessons.length === 0" class="text-center py-4 text-muted">
                  <i class="fas fa-chalkboard fa-2x mb-2"></i>
                  <p>{{ $t('teacherLessons.noRecords') }}</p>
                </div>
                <div v-for="lesson in lessons" :key="'mobile-' + lesson.id" class="card mb-3">
                  <div class="card-body py-2">
                    <div class="d-flex justify-content-between align-items-start">
                      <h6 class="mb-1 text-sm">{{ lesson.teacher?.name || '—' }}</h6>
                      <button class="btn btn-link text-danger p-0" @click="removeLesson(lesson)" v-can="'teacher_lessons.delete'">
                        <i class="fas fa-trash fa-sm"></i>
                      </button>
                    </div>
                    <p class="text-xs text-muted mb-1">
                      {{ lesson.class?.name || '—' }} / {{ lesson.group?.name || '—' }} / {{ lesson.subject?.name || '—' }}
                    </p>
                    <p class="text-xs text-muted mb-1">{{ $t('teacherLessons.date') }}: {{ lesson.lesson_date }}</p>
                    <p class="text-xs text-muted mb-2">{{ $t('teacherLessons.price') }}: {{ lesson.lesson_price }} {{ $t('common.currency') }}</p>
                    <select class="form-select form-select-sm" v-model="lesson.status" @change="updateStatus(lesson)">
                      <option value="attended">{{ $t('teacherLessons.attended') }}</option>
                      <option value="absent">{{ $t('teacherLessons.absent') }}</option>
                      <option value="cancelled">{{ $t('teacherLessons.cancelled') }}</option>
                    </select>
                  </div>
                </div>
              </div>

              <div class="d-flex justify-content-center mt-4">
                <Pagination :pagination="pagination" @page-changed="onPageChanged" />
              </div>
            </template>
          </div>
        </div>
      </div>
    </div>
  </template>
  <router-view></router-view>
</template>

<script setup>
import { ref } from "vue";
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";
import Pagination from "@/components/common/PaginationControls.vue";
import TeacherLessonFilters from "@/components/features/teacherLessons/filters/TeacherLessonFilters.vue";
import api from "@/api";

const { t } = useI18n();
const toast = useToast();

const lessons = ref([]);
const isLoading = ref(false);
const filters = ref({ date: '', status: '' });

const pagination = ref({ currentPage: 1, perPage: 15, totalItems: 0, totalPages: 0, from: 0, to: 0 });

const fetchLessons = async () => {
  isLoading.value = true;
  try {
    const params = { page: pagination.value.currentPage, per_page: pagination.value.perPage, ...filters.value };
    Object.keys(params).forEach(key => { if (params[key] === '' || params[key] === null) delete params[key]; });

    const response = await api.get('/teacher-lessons', { params });
    lessons.value = response.data.data;

    if (response.data.meta) {
      pagination.value = {
        currentPage: response.data.meta.current_page,
        perPage: response.data.meta.per_page,
        totalItems: response.data.meta.total,
        totalPages: response.data.meta.last_page,
        from: response.data.meta.from,
        to: response.data.meta.to
      };
    }
  } catch (error) {
    toast.error(error.response?.data?.message || t('teacherLessons.loadError'));
  } finally {
    isLoading.value = false;
  }
};

const updateStatus = async (lesson) => {
  try {
    await api.put(`/teacher-lessons/${lesson.id}`, { status: lesson.status, notes: lesson.notes });
    toast.success(t('teacherLessons.updated'));
  } catch (error) {
    toast.error(error.response?.data?.message || t('common.error'));
  }
};

const removeLesson = async (lesson) => {
  try {
    await api.delete(`/teacher-lessons/${lesson.id}`);
    toast.success(t('teacherLessons.deleted'));
    await fetchLessons();
  } catch (error) {
    toast.error(error.response?.data?.message || t('common.error'));
  }
};

const handleFilter = (filterValues) => {
  filters.value = filterValues;
  pagination.value.currentPage = 1;
  fetchLessons();
};

const onPageChanged = (page) => {
  pagination.value.currentPage = page;
  fetchLessons();
};

// No standalone onMounted fetch here: TeacherLessonFilters emits its default
// (today's date) filter on its own mount, which fires handleFilter() and
// triggers the initial fetch with that default already applied — avoids a
// duplicate request and keeps a single source of truth for the default.
</script>
